import { ReactNode } from 'react';

import {
  AlternativeModuleNames,
  ModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { ProductFeatureName } from 'dashboard/models/organization';
import IconAccountsModule from 'images/dashboard/case-modules/prebuilt/accounts-module.svg';
import IconButtonComponent from 'images/dashboard/case-modules/prebuilt/button-component.svg';
import IconCaseAttachmentsModule from 'images/dashboard/case-modules/prebuilt/case-attachments-module.svg';
import IconCaseModule from 'images/dashboard/case-modules/prebuilt/case-module.svg';
import IconChecklistModule from 'images/dashboard/case-modules/prebuilt/checklist-module.svg';
import IconCommentsModule from 'images/dashboard/case-modules/prebuilt/comments-module.svg';
import IconDataDisplayModule from 'images/dashboard/case-modules/prebuilt/data-display-module.svg';
import IconDevicesModule from 'images/dashboard/case-modules/prebuilt/devices-module.svg';
import IconEmailModule from 'images/dashboard/case-modules/prebuilt/email-module.svg';
import IconEventTimelineModule from 'images/dashboard/case-modules/prebuilt/event-timeline-module.svg';
import IconGraphModule from 'images/dashboard/case-modules/prebuilt/graph-module.svg';
import IconIframeModule from 'images/dashboard/case-modules/prebuilt/iframe-module.svg';
import IconInfoModule from 'images/dashboard/case-modules/prebuilt/info-module.svg';
import IconInputComponent from 'images/dashboard/case-modules/prebuilt/input-component.svg';
import IconInputSelectComponent from 'images/dashboard/case-modules/prebuilt/input-select-component.svg';
import IconInquiryModule from 'images/dashboard/case-modules/prebuilt/inquiry-module.svg';
import InsightsSummaryModule from 'images/dashboard/case-modules/prebuilt/insights-summary-module.svg';
import IconKeyValueModule from 'images/dashboard/case-modules/prebuilt/key-value-module.svg';
import IconListMatchesModule from 'images/dashboard/case-modules/prebuilt/list-matches-module.svg';
import IconLocationModule from 'images/dashboard/case-modules/prebuilt/location-module.svg';
import IconMarkdownModule from 'images/dashboard/case-modules/prebuilt/markdown-module.svg';
import IconAccountSelfieSimilarityModule from 'images/dashboard/case-modules/prebuilt/module-account-selfie-similarity.svg';
import IconRelatedCasesModule from 'images/dashboard/case-modules/prebuilt/related-cases-module.svg';
import IconReportsModule from 'images/dashboard/case-modules/prebuilt/reports-module.svg';
import IconSARModule from 'images/dashboard/case-modules/prebuilt/sar-module.svg';
import IconTableComponent from 'images/dashboard/case-modules/prebuilt/table-component.svg';
import IconTagsModule from 'images/dashboard/case-modules/prebuilt/tags-module.svg';
import IconTransactionsModule from 'images/dashboard/case-modules/prebuilt/transactions-module.svg';
import IconVerificationsModule from 'images/dashboard/case-modules/prebuilt/verifications-module.svg';
import { FeatureFlag } from 'lib/interfaces/feature-flags';

export interface ModuleTypeInfo<SupportedModuleTypes extends ModuleTypes> {
  type: SupportedModuleTypes;
  featureFlag: FeatureFlag | null;
  // a module will be displayed if any of the product features are enabled on the org and environment
  productFeatures: ProductFeatureName[] | null;
  internalUsersOnly: boolean;
}

// These modules are a subset of ModuleTypes that cases grid view currently supports. We use a regular javascript object
// NB: the ordering here will be the order in which modules show up in the Component Library.
export const CasesGridModuleTypes: Record<string, ModuleTypeInfo<ModuleTypes>> = {
  CASE_INFO: {
    type: ModuleTypes.CaseInfo,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  CASE_COMMENTS: {
    type: ModuleTypes.CaseComments,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  TAGS: {
    type: ModuleTypes.Tags,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  EVENT_TIMELINE: {
    type: ModuleTypes.EventTimeline,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  CHECKLIST: {
    type: ModuleTypes.Checklist,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INQUIRY: {
    type: ModuleTypes.Inquiry,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  REPORT: {
    type: ModuleTypes.Report,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  REPORT_RUN: {
    type: ModuleTypes.ReportRun,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  VERIFICATION: {
    type: ModuleTypes.Verification,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  LOCATION: {
    type: ModuleTypes.Location,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  DEVICE: {
    type: ModuleTypes.Device,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  GRAPH: {
    type: ModuleTypes.Graph,
    featureFlag: null,
    productFeatures: [ProductFeatureName.GraphV1],
    internalUsersOnly: false,
  },
  GRAPHV2: {
    type: ModuleTypes.Graphv2,
    featureFlag: null,
    productFeatures: [ProductFeatureName.Graph],
    internalUsersOnly: false,
  },
  KEY_VALUE: {
    type: ModuleTypes.KeyValue,
    featureFlag: null,
    productFeatures: [
      ProductFeatureName.CasesBasic,
      ProductFeatureName.CasesEnhanced,
      ProductFeatureName.CasesEnterprise,
    ],
    internalUsersOnly: false,
  },
  LIST_MATCHES: {
    type: ModuleTypes.ListMatches,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  CASE_ATTACHMENTS: {
    type: ModuleTypes.CaseAttachments,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  DATA_DISPLAY: {
    type: ModuleTypes.DataDisplay,
    featureFlag: null,
    productFeatures: [
      ProductFeatureName.CasesBasic,
      ProductFeatureName.CasesEnhanced,
      ProductFeatureName.CasesEnterprise,
    ],
    internalUsersOnly: false,
  },
  CASE_EMAIL: {
    type: ModuleTypes.CaseEmail,
    featureFlag: null,
    productFeatures: [
      ProductFeatureName.CasesModulesMessages,
      ProductFeatureName.CasesBasic,
      ProductFeatureName.CasesEnhanced,
      ProductFeatureName.CasesEnterprise,
    ],
    internalUsersOnly: false,
  },
  DATA_TABLE: {
    type: ModuleTypes.DataTable,
    featureFlag: 'dashboard:cases-data-table-module',
    productFeatures: null,
    internalUsersOnly: false,
  },
  MARKDOWN: {
    type: ModuleTypes.Markdown,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  AI_SUMMARY: {
    type: ModuleTypes.AISummary,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: true,
  },
  RELATED_CASES: {
    type: ModuleTypes.RelatedCases,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  TRANSACTION: {
    type: ModuleTypes.Transaction,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  ACCOUNT_SIMILARITY_SELFIE: {
    type: ModuleTypes.AccountSimilaritySelfie,
    featureFlag: null,
    productFeatures: [ProductFeatureName.AccountsSelfieSimilarity],
    internalUsersOnly: false,
  },
  SAR: {
    type: ModuleTypes.Sar,
    featureFlag: null,
    productFeatures: [ProductFeatureName.SARsDashboard],
    internalUsersOnly: false,
  },
  ACCOUNT: {
    type: ModuleTypes.Account,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  FIELD_UPDATE: {
    type: ModuleTypes.FieldUpdate,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INSIGHTS_SUMMARY: {
    type: ModuleTypes.InsightsSummary,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: true,
  },
  IFRAME: {
    type: ModuleTypes.Iframe,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: true,
  },
  LAYOUT_BY_ACCOUNT: {
    type: ModuleTypes.LayoutByAccount,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: true,
  },
};

export const CasesGridComponentTypes: Record<string, ModuleTypeInfo<ModuleTypes>> = {
  BUTTON: {
    type: ModuleTypes.Button,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INPUT: {
    type: ModuleTypes.Input,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INPUT_SELECT: {
    type: ModuleTypes.InputSelect,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  TABLE: {
    type: ModuleTypes.Table,
    featureFlag: 'dashboard:case-data-source-query',
    productFeatures: null,
    internalUsersOnly: false,
  },
};

export const FULL_HEIGHT_MODULES: ModuleTypes[] = [
  ModuleTypes.CaseEmail,
  ModuleTypes.Button,
  ModuleTypes.Input,
  ModuleTypes.InputSelect,
  ModuleTypes.Table,
  ModuleTypes.Iframe,
];

export const MODULE_ICON: Partial<Record<ModuleTypes, ReactNode>> = {
  [ModuleTypes.Account]: <IconAccountsModule />,
  [ModuleTypes.AccountInfo]: <IconInfoModule />,
  [ModuleTypes.Button]: <IconButtonComponent />,
  [ModuleTypes.CaseAttachments]: <IconCaseAttachmentsModule />,
  [ModuleTypes.CaseComments]: <IconCommentsModule />,
  [ModuleTypes.CaseEmail]: <IconEmailModule />,
  [ModuleTypes.CaseInfo]: <IconCaseModule />,
  [ModuleTypes.Checklist]: <IconChecklistModule />,
  [ModuleTypes.DataDisplay]: <IconDataDisplayModule />,
  [ModuleTypes.Device]: <IconDevicesModule />,
  [ModuleTypes.EventTimeline]: <IconEventTimelineModule />,
  [ModuleTypes.Graph]: <IconGraphModule />,
  [ModuleTypes.Graphv2]: <IconGraphModule />,
  [ModuleTypes.Input]: <IconInputComponent />,
  [ModuleTypes.InputSelect]: <IconInputSelectComponent />,
  [ModuleTypes.Inquiry]: <IconInquiryModule />,
  [ModuleTypes.Inquiryv2]: <IconInquiryModule />,
  [ModuleTypes.KeyValue]: <IconKeyValueModule />,
  [ModuleTypes.ListMatches]: <IconListMatchesModule />,
  [ModuleTypes.Location]: <IconLocationModule />,
  [ModuleTypes.Markdown]: <IconMarkdownModule />,
  [ModuleTypes.AISummary]: <IconMarkdownModule />,
  [ModuleTypes.Relations]: <IconTableComponent />,
  [ModuleTypes.Report]: <IconReportsModule />,
  [ModuleTypes.Reportv2]: <IconReportsModule />,
  [ModuleTypes.ReportRun]: <IconReportsModule />,
  [ModuleTypes.Tags]: <IconTagsModule />,
  [ModuleTypes.RelatedCases]: <IconRelatedCasesModule />,
  [ModuleTypes.Transaction]: <IconTransactionsModule />,
  [ModuleTypes.Verification]: <IconVerificationsModule />,
  [ModuleTypes.Verificationv2]: <IconVerificationsModule />,
  [ModuleTypes.AccountSimilaritySelfie]: <IconAccountSelfieSimilarityModule />,
  [ModuleTypes.Sar]: <IconSARModule />,
  [ModuleTypes.FieldUpdate]: <IconDataDisplayModule />,
  [ModuleTypes.InsightsSummary]: <InsightsSummaryModule />,
  [ModuleTypes.Table]: <IconTableComponent />,
  [ModuleTypes.Iframe]: <IconIframeModule />,
  [ModuleTypes.LayoutByAccount]: <IconAccountsModule />,
  [ModuleTypes.DataTable]: <IconDataDisplayModule />,
};

export const ALTERNATIVE_MODULE_TITLE: Record<AlternativeModuleNames, string> = {
  [AlternativeModuleNames.ComponentLibrary]: 'Component Library',
  [AlternativeModuleNames.Tabs]: 'Tabs',
};
